import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useAccount,
  useBalance,
  useConnect,
  useDisconnect,
  useReadContract,
  useReadContracts,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWatchContractEvent,
  useWriteContract,
} from "wagmi";
import { decodeEventLog, formatEther, maxUint256, parseEther, type Hex } from "viem";
import {
  activeChain,
  COLOR_WHEEL_ADDRESS,
  contractsConfigured,
  explorerAddress,
  explorerTx,
  shortAddress,
  walletConnectAvailable,
  WHEEL_TOKEN_ADDRESS,
} from "./lib/chain";
import {
  BETTING_WINDOW,
  COLORS,
  colorWheelAbi,
  ROUND_DURATION,
  wheelTokenAbi,
} from "./lib/contracts";
import { chipSound, loseSound, setMuted, spinSound } from "./lib/sound";
import {
  fetchProfiles,
  recordBets,
  recordRound,
  saveProfile,
  UsernameTakenError,
  type Profile,
} from "./lib/social";
import { completeXLogin, type XAccount } from "./lib/xAuth";
import { ColorWheel } from "./components/ColorWheel";
import { BetLog, type RecentRound } from "./components/BetLog";
import { Ticker } from "./components/Ticker";
import { ChatBox } from "./components/ChatBox";
import { ProfileModal } from "./components/ProfileModal";
import { WinCelebration } from "./components/WinCelebration";
import { LoseCelebration } from "./components/LoseCelebration";
import { AdminPanel } from "./components/AdminPanel";

type Phase = "idle" | "approving" | "placing" | "closing" | "claiming" | "refunding";
// The browser can no longer settle — that needs the keeper's secret seed. Its
// only self-serve fallbacks for a keeper-down round are close and refund.
type Action = "approve" | "place" | "close" | "claim" | "refund";

type LiveSpin = {
  roundId: bigint;
  result: number;
  winPool: bigint;
  totalStaked: bigint;
  players: number;
  entropy: Hex;
  entropyBlock: bigint;
};

// rounds() getter: totalStaked, maxColorStake, closeBlock, settleBlock, players,
// result, settled, voided, requested, entropy.
type RoundTuple = [bigint, bigint, bigint, bigint, number, number, boolean, boolean, boolean, Hex];

const CHIP_PRESETS = ["0.001", "0.005", "0.01", "0.05"] as const;
const NONE_SELECTED = COLORS.map(() => false);
/** House rules: at most 5 colors per round, equal stake on each. */
const MAX_COLORS = 5;
/** Contract caps a bet tx at 30 chips → 5 colors × 6. */
const MAX_STACK = 6;

/**
 * Round # shown in the UI. The on-chain roundId is a raw unix-minute number;
 * subtract the launch round (VITE_GENESIS_ROUND) so the very first live round
 * reads as #0. Falls back to the last 5 digits when no genesis is configured.
 */
const GENESIS_ROUND = (() => {
  try {
    return BigInt(import.meta.env.VITE_GENESIS_ROUND ?? "0");
  } catch {
    return 0n;
  }
})();
function roundNo(rid: bigint): string {
  return GENESIS_ROUND > 0n && rid >= GENESIS_ROUND
    ? (rid - GENESIS_ROUND).toString()
    : rid.toString().slice(-5);
}

export default function App() {
  const { address, isConnected, chainId } = useAccount();
  const { connect, connectors, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const { data: balance } = useBalance({ address });

  const {
    writeContract,
    data: txHash,
    isPending: isWriting,
    error: writeError,
    reset,
  } = useWriteContract();
  const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  const [theme, setTheme] = useState<"dark" | "light">(
    () => (localStorage.getItem("wheel-theme") as "dark" | "light") ?? "dark"
  );
  const [soundOn, setSoundOn] = useState(true);
  const [chipValue, setChipValue] = useState("0.001");
  const [selected, setSelected] = useState<boolean[]>(NONE_SELECTED);
  const [stack, setStack] = useState(1);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileFirstTime, setProfileFirstTime] = useState(false);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [walletMenu, setWalletMenu] = useState(false);
  const [walletPicker, setWalletPicker] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [celebration, setCelebration] = useState<{
    payout: bigint;
    multiplier: string;
    totalBet: bigint;
  } | null>(null);
  const [loseResult, setLoseResult] = useState<number | null>(null);
  // Live $WHEEL price (Dexscreener, highest-liquidity pool) so the entry burn
  // can be shown as its dollar value instead of a raw token count.
  const [wheelPriceUsd, setWheelPriceUsd] = useState<number | null>(null);
  useEffect(() => {
    if (!contractsConfigured) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(
          `https://api.dexscreener.com/latest/dex/tokens/${WHEEL_TOKEN_ADDRESS}`
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          pairs?: { priceUsd?: string; liquidity?: { usd?: number } }[];
        };
        const pairs = (data.pairs ?? [])
          .filter((x) => x.priceUsd && Number(x.priceUsd) > 0)
          .sort((a, b) => (b.liquidity?.usd ?? 0) - (a.liquidity?.usd ?? 0));
        if (!cancelled && pairs.length) setWheelPriceUsd(Number(pairs[0].priceUsd));
      } catch {
        /* price unavailable — fall back to the token count */
      }
    };
    void load();
    const iv = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, []);
  const promptedFor = useRef<string | null>(null);
  const walletMenuRef = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [lastAction, setLastAction] = useState<Action | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [myColorStakes, setMyColorStakes] = useState<Record<string, bigint[]>>({});
  const [claimedPayouts, setClaimedPayouts] = useState<Record<string, bigint>>({});
  const [watchRound, setWatchRound] = useState<bigint | null>(null);
  const [roundStuck, setRoundStuck] = useState(false);
  const [liveQueue, setLiveQueue] = useState<LiveSpin[]>([]);
  const [liveSpin, setLiveSpin] = useState<LiveSpin | null>(null);
  const [liveResult, setLiveResult] = useState<LiveSpin | null>(null);
  const [logKey, setLogKey] = useState(0);
  const liveSpinRef = useRef<LiveSpin | null>(null);
  const seenRounds = useRef<Set<string>>(new Set());
  // read inside enqueueSpin without making it re-created every render
  const myColorStakesRef = useRef<Record<string, bigint[]>>({});
  const addressRef = useRef<string | undefined>(undefined);
  const firedActions = useRef<Set<string>>(new Set());
  const lastFired = useRef<string | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("wheel-theme", theme);
  }, [theme]);

  useEffect(() => {
    setMuted(!soundOn);
  }, [soundOn]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    myColorStakesRef.current = myColorStakes;
  }, [myColorStakes]);

  useEffect(() => {
    addressRef.current = address;
  }, [address]);

  useEffect(() => {
    void fetchProfiles()
      .then(setProfiles)
      .catch(() => undefined)
      .finally(() => setProfilesLoaded(true));
  }, []);

  useEffect(() => {
    if (!walletMenu) return;
    const close = (e: MouseEvent) => {
      if (!walletMenuRef.current?.contains(e.target as Node)) setWalletMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [walletMenu]);

  const myProfile = address ? profiles[address.toLowerCase()] : undefined;

  const [pendingX, setPendingX] = useState<XAccount | null>(null);
  // True only on the page load we return to from X (?code=…). Suppresses the
  // first-connect profile prompt while the X identity is being attached.
  const xReturning = useRef(
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("code")
  );

  // Prompt for a profile the first time a wallet connects without one — but NOT
  // when connecting via X, which sets the username + avatar from the X account
  // automatically, so there's nothing to ask.
  useEffect(() => {
    if (!isConnected || !address || !profilesLoaded) return;
    if (xReturning.current || pendingX) return; // X login is providing the identity
    if (promptedFor.current === address) return;
    promptedFor.current = address;
    if (!profiles[address.toLowerCase()]?.username) {
      setProfileFirstTime(true);
      setProfileOpen(true);
    }
  }, [isConnected, address, profilesLoaded, profiles, pendingX]);

  // X login, step 1: as soon as we come back from X (?code=…), exchange the
  // code for the account EXACTLY ONCE — independent of the wallet, which
  // reconnects a beat later. Stash the verified account until the wallet is up.
  const xExchangeRan = useRef(false);
  useEffect(() => {
    if (xExchangeRan.current) return;
    xExchangeRan.current = true;
    void completeXLogin()
      .then((account) => {
        if (account) setPendingX(account);
        else xReturning.current = false;
      })
      .catch((e) => {
        xReturning.current = false;
        setStatus(e instanceof Error ? e.message : "X login failed");
      });
  }, []);

  // X login, step 2: once the wallet is connected, attach the verified handle
  // to the profile and pop it open.
  useEffect(() => {
    if (!address || !pendingX) return;
    const account = pendingX;
    setPendingX(null);
    void (async () => {
      let draft: { username?: string; avatarUrl?: string; bio?: string } = {};
      try {
        draft = JSON.parse(sessionStorage.getItem("wheel-profile-draft") ?? "{}");
      } catch {
        draft = {};
      }
      sessionStorage.removeItem("wheel-profile-draft");
      const current = profiles[address.toLowerCase()];
      // Connecting X takes over your identity: username + pic become the X ones.
      const desiredName = (account.name || account.handle || "").trim();
      const base = {
        address,
        avatarUrl: account.avatarUrl || draft.avatarUrl || current?.avatarUrl || "",
        bio: draft.bio ?? current?.bio ?? "",
        xHandle: account.handle,
        xVerified: true,
        updatedAt: Date.now(),
      };
      try {
        await saveProfile({ ...base, username: desiredName });
      } catch (e) {
        if (e instanceof UsernameTakenError) {
          // someone already uses that name — keep it unique so X still connects
          await saveProfile({ ...base, username: `${desiredName}_${address.slice(2, 6)}` });
        } else {
          throw e;
        }
      }
      setProfiles(await fetchProfiles());
      // X is the identity — bind it to the wallet silently, no profile prompt.
      xReturning.current = false;
      promptedFor.current = address;
      setStatus(`connected as @${account.handle}`);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, pendingX]);

  // Round length is READ from the contract so the UI always matches whatever is
  // deployed (a redeploy can change it). Falls back to the compiled constants
  // until the read lands.
  const { data: roundCfg } = useReadContracts({
    contracts: [
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "ROUND_DURATION" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "BETTING_WINDOW" },
    ],
    query: { enabled: contractsConfigured, staleTime: Infinity },
  });
  const roundCfgReady = roundCfg?.[0]?.result !== undefined;
  const roundSecs = Number(roundCfg?.[0]?.result ?? ROUND_DURATION);
  const bettingSecs = Number(roundCfg?.[1]?.result ?? BETTING_WINDOW);

  const nowSec = Math.floor(now / 1000);
  const curRid = BigInt(Math.floor(nowSec / roundSecs));
  const secondsInRound = nowSec % roundSecs;
  /** true during the locked tail: spin + result show */
  const inTail = secondsInRound >= bettingSecs;
  const secondsLeft = inTail ? 0 : bettingSecs - secondsInRound;
  const countdown = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

  const wrongNetwork = isConnected && chainId !== activeChain.id;
  const busy = phase !== "idle" || isWriting || isConfirming;

  const { data: onchain, refetch: refetchStats } = useReadContracts({
    contracts: [
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "treasuryBalance" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "maxBet" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "minBet" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "totalWins" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "totalPaidOut" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "burnPerBet" },
      { address: COLOR_WHEEL_ADDRESS, abi: colorWheelAbi, functionName: "totalWheelBurned" },
    ],
    query: { enabled: contractsConfigured, refetchInterval: 10_000 },
  });

  const chainTreasury = onchain?.[0]?.result as bigint | undefined;
  const chainMaxBet = onchain?.[1]?.result as bigint | undefined;
  const minBet = onchain?.[2]?.result as bigint | undefined;
  const chainWins = onchain?.[3]?.result as bigint | undefined;
  const chainPaidOut = onchain?.[4]?.result as bigint | undefined;
  const chainBurnPerBet = onchain?.[5]?.result as bigint | undefined;
  const chainBurned = onchain?.[6]?.result as bigint | undefined;

  // Admin gate: the panel unlocks ONLY for the contract owner's wallet. Every
  // action inside is onlyOwner/permissionless on-chain, so this is the real
  // login, not a cosmetic hide — a non-owner sees nothing and can execute
  // nothing.
  const { data: ownerAddr } = useReadContract({
    address: COLOR_WHEEL_ADDRESS,
    abi: colorWheelAbi,
    functionName: "owner",
    query: { enabled: contractsConfigured },
  });
  const isAdmin =
    !!address && !!ownerAddr && address.toLowerCase() === (ownerAddr as string).toLowerCase();

  useEffect(() => {
    if (isAdmin && window.location.hash === "#admin") setAdminOpen(true);
  }, [isAdmin]);

  const treasury = chainTreasury;
  const maxBet = chainMaxBet;
  const totalWins = chainWins;
  const totalPaidOut = chainPaidOut;
  const burnPerBet = chainBurnPerBet;
  const totalWheelBurned = chainBurned;

  const { data: roundData, refetch: refetchRoundNow } = useReadContracts({
    contracts: [
      {
        address: COLOR_WHEEL_ADDRESS,
        abi: colorWheelAbi,
        functionName: "rounds",
        args: [curRid],
      },
      {
        address: COLOR_WHEEL_ADDRESS,
        abi: colorWheelAbi,
        functionName: "getColorTotals",
        args: [curRid],
      },
      {
        address: COLOR_WHEEL_ADDRESS,
        abi: colorWheelAbi,
        functionName: "getPlayerStakes",
        args: [curRid, address ?? "0x0000000000000000000000000000000000000000"],
      },
      {
        address: COLOR_WHEEL_ADDRESS,
        abi: colorWheelAbi,
        functionName: "maxPositionsNow",
        args: [address ?? "0x0000000000000000000000000000000000000000"],
      },
    ],
    query: { enabled: contractsConfigured, refetchInterval: 4_000 },
  });

  const roundNowTuple = roundData?.[0]?.result as RoundTuple | undefined;
  const chainStaked = roundNowTuple?.[0] ?? 0n;
  const chainPlayers = roundNowTuple ? Number(roundNowTuple[4]) : 0;
  const chainColorTotals = (roundData?.[1]?.result as readonly bigint[] | undefined)?.slice();
  const chainMyStakes = (roundData?.[2]?.result as readonly bigint[] | undefined)?.slice();

  const myStakesNow = chainMyStakes;
  const colorTotalsNow = chainColorTotals;
  const stakedNow = chainStaked;
  const playersNow = chainPlayers;
  const myTotalNow = myStakesNow?.reduce((a, b) => a + b, 0n) ?? 0n;

  // Sliding pacing cap: bet 5 → max 4 next spin, 4 → 3, 3 → 2; bets of 1-2
  // never restrict.
  const capRead = roundData?.[3]?.result as bigint | undefined;
  const positionCap = Math.max(0, Math.min(MAX_COLORS, Number(capRead ?? BigInt(MAX_COLORS))));

  const { data: watchData, refetch: refetchWatch } = useReadContracts({
    contracts:
      watchRound !== null && address
        ? [
            {
              address: COLOR_WHEEL_ADDRESS,
              abi: colorWheelAbi,
              functionName: "rounds",
              args: [watchRound],
            },
            {
              address: COLOR_WHEEL_ADDRESS,
              abi: colorWheelAbi,
              functionName: "isRefundable",
              args: [watchRound],
            },
            {
              address: COLOR_WHEEL_ADDRESS,
              abi: colorWheelAbi,
              functionName: "claimable",
              args: [watchRound, address],
            },
          ]
        : [],
    query: {
      enabled: contractsConfigured && watchRound !== null && !!address,
      refetchInterval: 2_000,
    },
  });

  // Settled rounds from the chain, merged below with synthesized idle rounds
  // so the ticker and feed show a result for EVERY round.
  const { data: chainRoundsData } = useReadContract({
    address: COLOR_WHEEL_ADDRESS,
    abi: colorWheelAbi,
    functionName: "getRecentRounds",
    // polled frequently so it doubles as a reliable "live spin" feed for every
    // visitor even if the RoundSettled event/log subscription is flaky.
    query: { enabled: contractsConfigured, refetchInterval: 4_000 },
    scopeKey: `history-${logKey}`,
  });

  const { data: wheelData, refetch: refetchWheel } = useReadContracts({
    contracts: [
      {
        address: WHEEL_TOKEN_ADDRESS,
        abi: wheelTokenAbi,
        functionName: "balanceOf",
        args: address ? [address] : undefined,
      },
      {
        address: WHEEL_TOKEN_ADDRESS,
        abi: wheelTokenAbi,
        functionName: "allowance",
        args: address ? [address, COLOR_WHEEL_ADDRESS] : undefined,
      },
    ],
    query: { enabled: contractsConfigured && !!address, refetchInterval: 12_000 },
  });

  const wheelBalance = wheelData?.[0]?.result as bigint | undefined;
  const wheelAllowance = wheelData?.[1]?.result as bigint | undefined;
  const burnRequired = burnPerBet !== undefined && burnPerBet > 0n;
  const needsApproval =
    burnRequired && (wheelAllowance === undefined || wheelAllowance < burnPerBet);
  const hasWheel =
    !burnRequired || (wheelBalance !== undefined && wheelBalance >= burnPerBet);

  const selectedColors = useMemo(
    () => selected.map((s, i) => (s ? i : -1)).filter((i) => i >= 0),
    [selected]
  );
  // the live cap (0 = already in this round; selection then moot)
  const effectiveCap = positionCap > 0 ? positionCap : MAX_COLORS;
  const selectionFull = selectedColors.length >= effectiveCap;
  const chipWei = useMemo(() => {
    try {
      return parseEther(chipValue);
    } catch {
      return 0n;
    }
  }, [chipValue]);
  // Every selected color carries the identical stake.
  const stakePerColor = chipWei * BigInt(stack);
  const totalStake = stakePerColor * BigInt(selectedColors.length);

  // Effective payout multiple on your TOTAL bet: a hit pays 10× the ONE winning
  // position's stake, so spreading across N positions dilutes it to 10/N.
  // 1→10×, 2→5×, 3→3.3×, 4→2.5×, 5→2×. Winnings amount = stakePerColor × 10.
  const fmtMult = (n: number) => {
    if (n <= 0) return "10";
    const m = 10 / n;
    return Number.isInteger(m) ? String(m) : m.toFixed(1);
  };
  const multLabel = fmtMult(selectedColors.length || 1);

  // Treasury coverage: a winning position pays 10×, so no position may carry
  // more than a tenth of the treasury.
  const maxPerPosition = maxBet;
  const overMax =
    maxPerPosition !== undefined && stakePerColor > 0n && stakePerColor > maxPerPosition;

  const connectWallet = useCallback(() => setWalletPicker(true), []);

  /** Tap a segment: select it, or deselect if already on. Max 5 colors. */
  const toggleColor = (color: number) => {
    if (busy) return;
    setSelected((cur) => {
      if (cur[color]) {
        return cur.map((s, i) => (i === color ? false : s));
      }
      if (cur.filter(Boolean).length >= effectiveCap) return cur;
      chipSound();
      return cur.map((s, i) => (i === color ? true : s));
    });
  };

  /** Stake changes apply to every selected color equally. */
  const bumpStack = (delta: number) => {
    if (busy) return;
    setStack((s) => Math.min(MAX_STACK, Math.max(1, s + delta)));
  };

  const clearChips = () => {
    setSelected(NONE_SELECTED);
    setStack(1);
  };

  const enqueueSpin = useCallback(
    (s: LiveSpin, options?: { index?: boolean }) => {
      const key = s.roundId.toString();
      // Animate once per round, but always index real settlements — an idle
      // spin may have claimed the round id first in a race.
      const isNew = !seenRounds.current.has(key);
      if (isNew) {
        seenRounds.current.add(key);
        setLiveQueue((q) => [...q, s]);
      }

      // Index the settled round so history, totals and the leaderboard are
      // accurate without re-reading the chain. Duplicate reports are ignored
      // by the primary key, so every player's browser can safely do this.
      if (options?.index !== false) {
        // Green lands on the winning position — store it position-first.
        void recordRound({
          roundId: s.roundId,
          resultIndex: s.result,
          resultName: `Position ${s.result + 1}`,
          resultHex: COLORS[0].hex,
          totalStaked: s.totalStaked,
          winPool: s.winPool,
          players: s.players,
          entropy: s.entropy,
          entropyBlock: s.entropyBlock,
        }).then(() => {
          const stakes = myColorStakesRef.current[key];
          if (!stakes || !addressRef.current) return;
          void recordBets(
            stakes
              .map((amount, colorIndex) => ({
                roundId: s.roundId,
                address: addressRef.current!,
                colorIndex,
                amount,
              }))
              .filter((b) => b.amount > 0n),
            s.result
          );
        });
      }
    },
    []
  );

  // Every visitor watches every round settle live — one shared spin per minute.
  useWatchContractEvent({
    address: COLOR_WHEEL_ADDRESS,
    abi: colorWheelAbi,
    eventName: "RoundSettled",
    enabled: contractsConfigured,
    onLogs(logs) {
      for (const log of logs) {
        const a = log.args as {
          roundId?: bigint;
          result?: number;
          entropy?: Hex;
          entropyBlock?: bigint;
          totalStaked?: bigint;
          winPool?: bigint;
          players?: number;
        };
        if (a.roundId === undefined) continue;
        enqueueSpin({
          roundId: a.roundId,
          result: Number(a.result ?? 0),
          winPool: a.winPool ?? 0n,
          totalStaked: a.totalStaked ?? 0n,
          players: Number(a.players ?? 0),
          entropy: a.entropy ?? ("0x" as Hex),
          entropyBlock: a.entropyBlock ?? 0n,
        });
      }
      setLogKey((k) => k + 1);
    },
  });

  // Reliable backup for the live spin: the getRecentRounds poll (every 4s)
  // enqueues any newly-settled round the event watcher missed, so EVERY visitor
  // sees each spin even on a flaky RPC. On first load we mark existing rounds
  // as already-seen so we don't replay all of history.
  const historyInit = useRef(false);
  useEffect(() => {
    const rounds = chainRoundsData as RecentRound[] | undefined;
    if (!rounds) return;
    if (!historyInit.current) {
      for (const r of rounds) seenRounds.current.add(r.roundId.toString());
      historyInit.current = true;
      return;
    }
    // oldest → newest so queued spins play in order
    for (const r of [...rounds].reverse()) {
      if (seenRounds.current.has(r.roundId.toString())) continue;
      enqueueSpin({
        roundId: r.roundId,
        result: Number(r.result),
        winPool: r.winPool,
        totalStaked: r.totalStaked,
        players: Number(r.players),
        entropy: r.entropy,
        entropyBlock: 0n,
      });
    }
  }, [chainRoundsData, enqueueSpin]);

  useEffect(() => {
    if (liveSpin !== null || liveQueue.length === 0) return;
    const [next, ...rest] = liveQueue;
    setLiveQueue(rest);
    setLiveResult(null);
    liveSpinRef.current = next;
    setLiveSpin(next);
    spinSound();
  }, [liveSpin, liveQueue]);

  // The wheel spins EVERY round, even when nobody bet: the moment the betting
  // window locks, empty rounds get a deterministic idle spin (derived from the
  // round id, identical on every screen, never green). Rounds with bets wait
  // for their real on-chain settlement instead.
  const tailHandled = useRef<string>("");
  useEffect(() => {
    if (!inTail) return;
    const key = curRid.toString();
    if (tailHandled.current === key) return;
    tailHandled.current = key;
    if (chainStaked > 0n) return; // real settlement will animate this round
    if (watchRound !== null && watchRound === curRid) return; // we bet it
    enqueueSpin(
      {
        roundId: curRid,
        result: Number(curRid % 10n),
        winPool: 0n,
        totalStaked: 0n,
        players: 0,
        entropy: "0x" as Hex,
        entropyBlock: 0n,
      },
      { index: false }
    );
  }, [inTail, curRid, chainStaked, watchRound, enqueueSpin]);

  const onSpinEnd = useCallback(() => {
    if (liveSpinRef.current) {
      setLiveResult(liveSpinRef.current);
      liveSpinRef.current = null;
      setLiveSpin(null);
    }
  }, []);

  useEffect(() => {
    if (!liveResult) return;
    const stakes = myColorStakes[liveResult.roundId.toString()];
    if (stakes) {
      const onResult = stakes[liveResult.result] ?? 0n;
      if (onResult > 0n) {
        // the celebration overlay plays its own fanfare + share card
        const positions = stakes.filter((s) => s > 0n).length;
        const total = stakes.reduce((a, b) => a + b, 0n);
        setCelebration({
          payout: onResult * 10n,
          multiplier: fmtMult(positions),
          totalBet: total,
        });
      } else if (stakes.some((s) => s > 0n)) {
        loseSound();
        setLoseResult(liveResult.result);
      }
    }
    const t = setTimeout(() => setLiveResult(null), 12_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveResult]);

  const submitPlaceBet = useCallback(() => {
    // Repeat each selected color `stack` times so the contract's even split
    // gives every color an identical stake.
    const colors: number[] = [];
    for (const c of selectedColors) {
      for (let k = 0; k < stack; k++) colors.push(c);
    }
    if (colors.length === 0) return;
    setPhase("placing");
    setLastAction("place");
    setStatus("confirm your chips in wallet…");
    writeContract({
      address: COLOR_WHEEL_ADDRESS,
      abi: colorWheelAbi,
      functionName: "placeBets",
      args: [colors],
      value: chipWei * BigInt(colors.length),
      chainId: activeChain.id,
    });
  }, [selectedColors, stack, chipWei, writeContract]);

  const fire = useCallback(
    (action: Exclude<Action, "approve" | "place">, rid: bigint) => {
      const key = `${rid.toString()}:${action}`;
      if (firedActions.current.has(key)) return;
      firedActions.current.add(key);
      lastFired.current = key;
      reset();
      if (action === "close") {
        setPhase("closing");
        setLastAction("close");
        setStatus("locking the round (sets its entropy block)…");
        writeContract({
          address: COLOR_WHEEL_ADDRESS,
          abi: colorWheelAbi,
          functionName: "closeRound",
          args: [rid],
          chainId: activeChain.id,
        });
      } else if (action === "refund") {
        setPhase("refunding");
        setLastAction("refund");
        setStatus("keeper down — reclaiming your stake in wallet…");
        writeContract({
          address: COLOR_WHEEL_ADDRESS,
          abi: colorWheelAbi,
          functionName: "claimRefund",
          args: [rid],
          chainId: activeChain.id,
        });
      } else {
        setPhase("claiming");
        setLastAction("claim");
        setStatus("claiming your 10× in wallet…");
        writeContract({
          address: COLOR_WHEEL_ADDRESS,
          abi: colorWheelAbi,
          functionName: "claim",
          args: [rid],
          chainId: activeChain.id,
        });
      }
    },
    [reset, writeContract]
  );

  // You sign ONCE (the bet). The keeper bot closes, settles, AND pushes your
  // payout — so the browser never auto-prompts another signature. This effect
  // only WATCHES the round you bet in: it stops watching once you've been
  // settled/paid, and flags a round as "stuck" (for a manual rescue button)
  // only if the keeper is clearly down — never signing anything on its own.
  useEffect(() => {
    if (watchRound === null) {
      setRoundStuck(false);
      return;
    }
    const round = watchData?.[0]?.result as RoundTuple | undefined;
    const claimableAmt = watchData?.[2]?.result as bigint | undefined;
    const settled = round ? round[6] : false;

    if (settled) {
      setRoundStuck(false);
      // once the keeper has settled and (for winners) pushed the payout, we owe
      // nothing more — stop watching.
      if (claimableAmt !== undefined && claimableAmt === 0n) setWatchRound(null);
      return;
    }
    // Not settled yet. The keeper handles it during the tail. Only if a LATER
    // round has already started and this one is still unsettled is the keeper
    // clearly down — surface a manual rescue, but never auto-sign.
    setRoundStuck(curRid > watchRound);
  }, [watchRound, curRid, watchData]);

  // Manual rescue for the rare keeper-down case. The browser can't settle (only
  // the keeper holds the seed), so it can only lock the round and, once the round
  // is unsettleable, reclaim the stake. Winners still self-claim. Opt-in only.
  const manualAdvance = () => {
    if (watchRound === null || busy) return;
    const round = watchData?.[0]?.result as RoundTuple | undefined;
    const refundable = watchData?.[1]?.result as boolean | undefined;
    const claimableAmt = watchData?.[2]?.result as bigint | undefined;
    if (!round) return;
    const closeBlock = round[2];
    const settled = round[6];
    // let a manual retry re-fire steps for this round
    for (const a of ["close", "refund", "claim"]) {
      firedActions.current.delete(`${watchRound.toString()}:${a}`);
    }
    if (settled) {
      if (claimableAmt && claimableAmt > 0n) fire("claim", watchRound);
      else setWatchRound(null);
    } else if (refundable) {
      fire("refund", watchRound);
    } else if (closeBlock === 0n) {
      fire("close", watchRound);
    } else {
      setStatus("waiting for the keeper to settle…");
    }
  };

  useEffect(() => {
    if (!isSuccess || !receipt || !txHash || !lastAction) return;

    if (lastAction === "approve") {
      void refetchWheel();
      reset();
      submitPlaceBet();
      return;
    }

    if (lastAction === "place") {
      for (const log of receipt.logs) {
        try {
          const decoded = decodeEventLog({
            abi: colorWheelAbi,
            data: log.data,
            topics: log.topics,
          });
          if (decoded.eventName === "BetPlaced") {
            const a = decoded.args as {
              roundId: bigint;
              colors: readonly number[];
              unitAmount: bigint;
            };
            const key = a.roundId.toString();
            setMyColorStakes((m) => {
              const cur = m[key] ? [...m[key]] : COLORS.map(() => 0n);
              for (const c of a.colors) cur[Number(c)] += a.unitAmount;
              return { ...m, [key]: cur };
            });
            setWatchRound(a.roundId);
            setPhase("idle");
            setLastAction(null);
            setStatus("chips are down — the wheel spins when the minute ends");
            clearChips();
            void refetchStats();
            void refetchWheel();
            void refetchRoundNow();
            return;
          }
        } catch {
          // not our event
        }
      }
      setPhase("idle");
      setLastAction(null);
      return;
    }

    if (lastAction === "close") {
      setPhase("idle");
      setLastAction(null);
      setStatus("round locked — waiting one block for entropy…");
      void refetchWatch();
      return;
    }

    if (lastAction === "claim" || lastAction === "refund") {
      if (lastAction === "refund") setWatchRound(null);
      for (const log of receipt.logs) {
        try {
          const decoded = decodeEventLog({
            abi: colorWheelAbi,
            data: log.data,
            topics: log.topics,
          });
          if (decoded.eventName === "RoundSettled") {
            const a = decoded.args as {
              roundId: bigint;
              result: number;
              entropy: Hex;
              entropyBlock: bigint;
              totalStaked: bigint;
              winPool: bigint;
              players: number;
            };
            enqueueSpin({
              roundId: a.roundId,
              result: Number(a.result),
              winPool: a.winPool,
              totalStaked: a.totalStaked,
              players: Number(a.players),
              entropy: a.entropy,
              entropyBlock: a.entropyBlock,
            });
          }
          if (decoded.eventName === "PayoutClaimed") {
            const a = decoded.args as { roundId: bigint; player: Hex; amount: bigint };
            if (address && a.player.toLowerCase() === address.toLowerCase()) {
              setClaimedPayouts((m) => ({ ...m, [a.roundId.toString()]: a.amount }));
            }
          }
        } catch {
          // not our event
        }
      }
      setPhase("idle");
      setLastAction(null);
      setStatus(null);
      void refetchStats();
      void refetchWatch();
      setLogKey((k) => k + 1);
      return;
    }
  }, [
    isSuccess,
    receipt,
    txHash,
    lastAction,
    address,
    refetchStats,
    refetchWheel,
    refetchRoundNow,
    refetchWatch,
    reset,
    submitPlaceBet,
    enqueueSpin,
  ]);

  useEffect(() => {
    if (!writeError) return;
    setStatus(writeError.message.split("\n")[0] ?? "transaction failed");
    setPhase("idle");
    setLastAction(null);
  }, [writeError]);


  const canPlace = useMemo(() => {
    if (!contractsConfigured || !isConnected || wrongNetwork || busy) return false;
    if (secondsLeft <= 5) return false;
    if (!hasWheel) return false;
    if (myTotalNow > 0n) return false; // one bet per round — you're already in
    if (selectedColors.length < 1) return false;
    if (selectedColors.length > positionCap) return false;
    if (chipWei <= 0n) return false;
    if (minBet && chipWei < minBet) return false;
    if (overMax) return false;
    if (balance && totalStake > balance.value) return false;
    return true;
  }, [
    isConnected,
    wrongNetwork,
    busy,
    secondsLeft,
    hasWheel,
    selectedColors,
    chipWei,
    minBet,
    overMax,
    positionCap,
    myTotalNow,
    balance,
    totalStake,
  ]);

  const handlePlace = () => {
    if (!canPlace) return;
    reset();
    if (needsApproval) {
      setPhase("approving");
      setLastAction("approve");
      setStatus("approve the WHEEL entry burn in wallet…");
      writeContract({
        address: WHEEL_TOKEN_ADDRESS,
        abi: wheelTokenAbi,
        functionName: "approve",
        args: [COLOR_WHEEL_ADDRESS, maxUint256],
        chainId: activeChain.id,
      });
      return;
    }
    submitPlaceBet();
  };

  // One row per finished round: real settlement when the chain has one,
  // otherwise the deterministic idle result every client can reconstruct.
  const history = useMemo<RecentRound[]>(() => {
    const real = new Map(
      ((chainRoundsData as RecentRound[] | undefined) ?? []).map((r) => [
        r.roundId.toString(),
        r,
      ])
    );
    const newest = inTail ? curRid : curRid - 1n;
    const out: RecentRound[] = [];
    for (let i = 0n; i < 50n && newest - i > 0n; i++) {
      const rid = newest - i;
      const settled = real.get(rid.toString());
      if (settled) {
        out.push(settled);
      } else if (rid !== curRid || !inTail || chainStaked === 0n) {
        out.push({
          roundId: rid,
          totalStaked: 0n,
          winPool: 0n,
          settleBlock: 0n,
          players: 0,
          result: Number(rid % 10n),
          won: false,
          entropy: "0x" as Hex,
        });
      }
    }
    return out;
  }, [chainRoundsData, curRid, inTail, chainStaked]);

  const myLastOutcome = useMemo(() => {
    if (!liveResult) return null;
    const key = liveResult.roundId.toString();
    const stakes = myColorStakes[key];
    if (!stakes) return null;
    const total = stakes.reduce((a, b) => a + b, 0n);
    if (total === 0n) return null;
    const onResult = stakes[liveResult.result] ?? 0n;
    return {
      ...liveResult,
      stakeOnResult: onResult,
      totalStake: total,
      lostStake: total - onResult,
      payout: onResult * 10n,
      claimed: claimedPayouts[key],
    };
  }, [liveResult, myColorStakes, claimedPayouts]);

  const buttonLabel = (() => {
    if (!isConnected) return isConnecting ? "CONNECTING…" : "SELECT WALLET";
    if (wrongNetwork) return isSwitching ? "SWITCHING…" : `SWITCH TO ${activeChain.name.toUpperCase()}`;
    if (phase === "approving" || (busy && lastAction === "approve")) return "APPROVING WHEEL…";
    if (phase === "placing" || (busy && lastAction === "place")) return "PLACING CHIPS…";
    if (phase === "closing" || (busy && lastAction === "close")) return "LOCKING ROUND…";
    if (phase === "refunding" || (busy && lastAction === "refund")) return "REFUNDING…";
    if (phase === "claiming" || (busy && lastAction === "claim")) return "CLAIMING 10×…";
    if (inTail) return "SPINNING — NEXT ROUND SOON";
    if (myTotalNow > 0n) return `CHIPS DOWN — SPIN IN ${countdown}`;
    if (selectedColors.length > positionCap)
      return `MAX ${positionCap} POSITION${positionCap === 1 ? "" : "S"} THIS SPIN`;
    if (secondsLeft <= 5) return `ROUND LOCKS IN ${secondsLeft}S`;
    if (burnRequired && !hasWheel)
      return `NEED ${Number(formatEther(burnPerBet)).toLocaleString()} WHEEL`;
    if (overMax && maxPerPosition !== undefined && selectedColors.length > 0)
      return `MAX ${formatEther(maxPerPosition)} ETH PER POSITION`;
    if (selectedColors.length === 0) return "TAP THE WHEEL TO PICK POSITIONS";
    return `BET ${formatEther(totalStake)} ETH · ${multLabel}×`;
  })();

  return (
    <div className="page">
      <Ticker refreshKey={logKey} override={history} />

      <header className="topbar">
        <div className="topbar-left">
          <button
            className="ctl-btn"
            onClick={() => setSoundOn((s) => !s)}
            title={soundOn ? "Mute" : "Unmute"}
          >
            {soundOn ? "SOUND ON" : "SOUND OFF"}
          </button>
          <button
            className="ctl-btn"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
          >
            {theme === "dark" ? "LIGHT" : "DARK"}
          </button>
          {isAdmin && (
            <button className="ctl-btn admin" onClick={() => setAdminOpen(true)} title="Owner controls">
              ⚙️ ADMIN
            </button>
          )}
        </div>
        <div className="topbar-right">
          {isConnected ? (
            <>
              {wrongNetwork ? (
                <button
                  className="ctl-btn warn"
                  onClick={() => switchChain({ chainId: activeChain.id })}
                  disabled={isSwitching}
                >
                  SWITCH NETWORK
                </button>
              ) : (
                <span className="ctl-pill">
                  {balance ? `${Number(formatEther(balance.value)).toFixed(4)} ETH` : "…"}
                </span>
              )}
              <div className="wallet-menu-wrap" ref={walletMenuRef}>
                <button className="ctl-btn" onClick={() => setWalletMenu((o) => !o)}>
                  {myProfile?.username || shortAddress(address!)} ▾
                </button>
                {walletMenu && (
                  <div className="wallet-menu">
                    <span className="wallet-menu-addr">{shortAddress(address!)}</span>
                    <button
                      onClick={() => {
                        setWalletMenu(false);
                        setProfileFirstTime(false);
                        setProfileOpen(true);
                      }}
                    >
                      PROFILE
                    </button>
                    <button
                      className="danger"
                      onClick={() => {
                        setWalletMenu(false);
                        disconnect();
                      }}
                    >
                      DISCONNECT
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <button className="ctl-btn" onClick={connectWallet} disabled={isConnecting}>
              {isConnecting ? "CONNECTING…" : "CONNECT"}
            </button>
          )}
        </div>
      </header>

      <main className="main">
        <h1 className="headline">
          <span className="lime">$WHEEL</span> — THE FIRST EVER{" "}
          <span className="lime">ZERO-HOUSE-EDGE</span> APP
        </h1>
        <p className="subline">
          One spin every 90 seconds. Pick your positions. Land on Green and 10× your bet. Provably
          fair and verifiable on-chain.
        </p>

        <div className="round-strip">
          <span className="round-clock">{inTail ? "SPIN" : countdown}</span>
          <span className="round-info">
            round #{roundCfgReady ? roundNo(curRid) : "…"} · {playersNow} player
            {playersNow === 1 ? "" : "s"} · {Number(formatEther(stakedNow)).toFixed(4)} ETH
            staked
            {myTotalNow > 0n && <> · you {formatEther(myTotalNow)} ETH</>}
          </span>
        </div>

        <div className="game-area">
        <div className="stage">
          {!liveSpin && !liveResult && (
            <p className="stage-badge">
              {selectedColors.length > 0 ? `HIT GREEN FOR ${multLabel}×` : "HIT GREEN · UP TO 10×"}
            </p>
          )}
          {liveSpin && (
            <div className="live-banner">
              <span className="live-dot" aria-hidden />
              LIVE · round #{roundNo(liveSpin.roundId)} · {liveSpin.players} player
              {liveSpin.players === 1 ? "" : "s"} ·{" "}
              {Number(formatEther(liveSpin.totalStaked)).toFixed(4)} ETH
            </div>
          )}
          {!liveSpin && liveResult && (() => {
            const idle = liveResult.entropy.length <= 2;
            const won = liveResult.winPool > 0n;
            const pos = liveResult.result + 1;
            return (
              <div className={`live-banner ${won ? "live-win" : ""}`}>
                <span className="feed-coin" style={{ background: COLORS[0].hex }} />
                <span style={{ color: COLORS[0].hex }}>GREEN landed on P{pos}</span>
                {" · "}
                {won
                  ? `${Number(formatEther(liveResult.winPool * 10n)).toFixed(4)} ETH paid at 10×`
                  : idle
                    ? "no bets this round"
                    : "nobody was on it"}
              </div>
            );
          })()}
          <ColorWheel
            spinning={liveSpin !== null}
            targetIndex={liveSpin ? liveSpin.result : liveResult ? liveResult.result : null}
            onSpinEnd={onSpinEnd}
            highlightWin={(liveResult?.winPool ?? 0n) > 0n}
            selected={selected}
            stackPerColor={stack}
            stakePerColor={stakePerColor}
            selectionFull={selectionFull}
            colorTotals={colorTotalsNow}
            myStakes={myStakesNow}
            onSegmentClick={toggleColor}
            interactive={!busy && liveSpin === null && myTotalNow === 0n}
          />
          <p className="stage-hint">
            {liveSpin
              ? "spinning…"
              : selectionFull
                ? `${effectiveCap} of ${effectiveCap} positions picked — tap one to swap it out`
                : `tap up to ${effectiveCap} positions · GREEN lands on yours → 10×`}
          </p>
        </div>

        <section className="controls">
          {!contractsConfigured && (
            <div className="notice">
              contracts not configured — deploy, then set{" "}
              <code>VITE_COLOR_WHEEL_ADDRESS</code> and <code>VITE_WHEEL_TOKEN_ADDRESS</code>
            </div>
          )}

          <div className="chip-value-row">
            <span className="row-label">BET AMOUNT</span>
            <input
              className="chip-value-input"
              inputMode="decimal"
              value={chipValue}
              onChange={(e) => setChipValue(e.target.value)}
              disabled={busy}
            />
            {CHIP_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={`preset ${chipValue === p ? "active" : ""}`}
                onClick={() => setChipValue(p)}
                disabled={busy}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            className="cta"
            onClick={() => {
              if (!isConnected) {
                connectWallet();
                return;
              }
              if (wrongNetwork) {
                switchChain({ chainId: activeChain.id });
                return;
              }
              handlePlace();
            }}
            disabled={
              isConnected && !wrongNetwork ? !canPlace : isConnecting || isSwitching
            }
          >
            {buttonLabel}
          </button>

          <p className="fine-print">
            {[
              maxPerPosition !== undefined
                ? `max ${Number(formatEther(maxPerPosition)).toFixed(4)} ETH per position`
                : null,
              burnRequired && isConnected && wheelBalance !== undefined
                ? `yours ${Number(formatEther(wheelBalance)).toLocaleString()} $WHEEL`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            <span className="bet-info">
              <button type="button" className="info-btn" aria-label="How betting works">
                i
              </button>
              <span className="info-box" role="tooltip">
                {burnRequired && (
                  <span className="info-row">
                    <span>Entry burn</span>
                    <b>
                      {wheelPriceUsd !== null && burnPerBet !== undefined
                        ? `~$${(Number(formatEther(burnPerBet)) * wheelPriceUsd).toFixed(2)} of $WHEEL / bet`
                        : `${Number(formatEther(burnPerBet ?? 0n)).toLocaleString()} $WHEEL / bet`}
                    </b>
                  </span>
                )}
                <span className="info-note">
                  Fewer positions = bigger multiplier — green pays 10× the ONE hit position,
                  split across your picks: 1 = 10× · 2 = 5× · 3 = 3.3× · 4 = 2.5× · 5 = 2×
                </span>
              </span>
            </span>
          </p>

          {selectedColors.length > 0 && (
            <div className="chip-tray">
              <div className="chip-tray-line">
                <span className="row-label">STAKE</span>
                <button
                  type="button"
                  className="stepper-btn"
                  onClick={() => bumpStack(-1)}
                  disabled={busy || stack <= 1}
                >
                  −
                </button>
                <span className="stake-amount">{formatEther(stakePerColor)} ETH</span>
                <button
                  type="button"
                  className="stepper-btn"
                  onClick={() => bumpStack(1)}
                  disabled={busy || stack >= MAX_STACK}
                >
                  +
                </button>
                <span className="tray-mult">{multLabel}×</span>
              </div>
              <div className="chip-tray-line">
                {selectedColors.map((i) => (
                  <button
                    key={i}
                    type="button"
                    className="chip-chip"
                    onClick={() => toggleColor(i)}
                    disabled={busy}
                    title="Remove this position"
                  >
                    <span className="pos-tag">P{i + 1}</span>
                    <span className="chip-chip-x">×</span>
                  </button>
                ))}
                <button type="button" className="preset" onClick={clearChips} disabled={busy}>
                  CLEAR
                </button>
              </div>
              <div className="tray-payout-row">
                <span>
                  total bet <strong>{formatEther(totalStake)} ETH</strong>
                </span>
                <span className="tray-win">
                  hit green → <strong>+{formatEther(stakePerColor * 10n)} ETH</strong> ({multLabel}×)
                </span>
              </div>
            </div>
          )}

          {status && <p className="status">{status}</p>}
          {roundStuck && watchRound !== null && (
            <div className="stuck-notice">
              <span>the keeper is slow — your round hasn't settled. rescue it yourself:</span>
              <button type="button" className="preset" onClick={manualAdvance} disabled={busy}>
                SETTLE MY ROUND
              </button>
            </div>
          )}

          {myLastOutcome && (
            <div className={`outcome ${myLastOutcome.stakeOnResult > 0n ? "win" : "lose"}`}>
              <strong>
                round #{roundNo(myLastOutcome.roundId)} ·{" "}
                {myLastOutcome.stakeOnResult > 0n
                  ? `GREEN on P${myLastOutcome.result + 1}`
                  : `GREEN landed on P${myLastOutcome.result + 1}`}
              </strong>
              {myLastOutcome.stakeOnResult > 0n ? (
                <span>
                  your position hit — {formatEther(myLastOutcome.payout)} ETH at 10×
                  {myLastOutcome.claimed !== undefined ? " · paid" : " · claiming…"}
                  {myLastOutcome.lostStake > 0n &&
                    ` · ${formatEther(myLastOutcome.lostStake)} ETH on other positions lost`}
                </span>
              ) : (
                <span>
                  nobody wins — your {formatEther(myLastOutcome.totalStake)} ETH joins the
                  treasury
                </span>
              )}
            </div>
          )}

          {liveResult && liveResult.entropy.length > 2 && (
            <details className="fairness">
              <summary>fairness proof</summary>
              <div className="feed-proof">
                <p>
                  <code>keccak256(blockhash(closeBlock + 1), roundId) % 10</code>
                </p>
                <p>
                  <strong>round</strong> {liveResult.roundId.toString()}
                </p>
                <p>
                  <strong>entropy block</strong> {liveResult.entropyBlock.toString()}
                </p>
                <p>
                  <strong>entropy</strong> <code>{liveResult.entropy}</code>
                </p>
              </div>
            </details>
          )}

          {txHash && (
            <a className="tx-link" href={explorerTx(txHash)} target="_blank" rel="noreferrer">
              view transaction ↗
            </a>
          )}
        </section>
        </div>

        <section className="stats">
          <div>
            <span>TREASURY</span>
            <strong>
              {treasury !== undefined ? `${Number(formatEther(treasury)).toFixed(4)} ETH` : "—"}
            </strong>
          </div>
          <div>
            <span>MAX BET</span>
            <strong>
              {maxBet !== undefined ? `${Number(formatEther(maxBet)).toFixed(4)} ETH` : "—"}
            </strong>
          </div>
          <div>
            <span>TOTAL PRIZES WON</span>
            <strong>
              {totalPaidOut !== undefined
                ? `${Number(formatEther(totalPaidOut)).toFixed(3)} ETH`
                : "—"}
            </strong>
          </div>
          <div>
            <span>ROUNDS WON</span>
            <strong>{totalWins !== undefined ? totalWins.toString() : "—"}</strong>
          </div>
          <div>
            <span>$WHEEL BURNT</span>
            <strong>
              {totalWheelBurned !== undefined
                ? Number(formatEther(totalWheelBurned)).toLocaleString()
                : "—"}
            </strong>
          </div>
        </section>

        <section className="how" id="how">
          <h2 className="section-title">HOW IT WORKS</h2>
          <div className="how-grid">
            <div className="how-card">
              <span className="how-step">01</span>
              <h3>PICK YOUR POSITIONS</h3>
              <p>
                The wheel has 10 positions. Tap up to <strong>5</strong> to drop your chips — every
                position carries the same stake. More positions means better odds but a smaller
                multiplier on your total bet:
              </p>
              <div className="mult-table">
                <div><span>1</span><strong>10×</strong></div>
                <div><span>2</span><strong>5×</strong></div>
                <div><span>3</span><strong>3.3×</strong></div>
                <div><span>4</span><strong>2.5×</strong></div>
                <div><span>5</span><strong>2×</strong></div>
              </div>
            </div>
            <div className="how-card">
              <span className="how-step">02</span>
              <h3>THE WHEEL SPINS</h3>
              <p>
                One shared spin every <strong>90 seconds</strong> — everyone plays the same round.
                When betting closes, the arrow spins and lands on a position. Land{" "}
                <strong>Robinhood Green</strong> on one of yours and that chip pays a true{" "}
                <strong>10×</strong>. Any other colour, the round moves on.
              </p>
            </div>
            <div className="how-card">
              <span className="how-step">03</span>
              <h3>ZERO HOUSE EDGE</h3>
              <p>
                1-in-10 odds paying 10× means <strong>EV = 1</strong> — no vig, no rake, no edge
                shaved off your bet. The treasury only survives on{" "}
                <strong>$WHEEL trading fees</strong>, making this the first ever zero-house-edge
                app. Bet limits scale with the treasury so every 10× is always covered.
              </p>
            </div>
            <div className="how-card">
              <span className="how-step">04</span>
              <h3>PROVABLY FAIR</h3>
              <p>
                The result comes from{" "}
                <code>keccak256(blockhash(closeBlock + 1), roundId) % 10</code> — a block that did
                not exist when you bet, so no one can predict or rig it. <strong>closeBlock is
                locked forever</strong>: no re-rolls. Every spin is recomputable and verifiable
                on-chain from the RECENT SPINS log below.
              </p>
            </div>
          </div>
          {contractsConfigured && (
            <p className="contracts">
              game{" "}
              <a href={explorerAddress(COLOR_WHEEL_ADDRESS)} target="_blank" rel="noreferrer">
                {shortAddress(COLOR_WHEEL_ADDRESS)}
              </a>
              {WHEEL_TOKEN_ADDRESS !== "0x0000000000000000000000000000000000000000" && (
                <>
                  {" · "}
                  $WHEEL{" "}
                  <a href={explorerAddress(WHEEL_TOKEN_ADDRESS)} target="_blank" rel="noreferrer">
                    {shortAddress(WHEEL_TOKEN_ADDRESS)}
                  </a>
                </>
              )}
            </p>
          )}
        </section>

        <BetLog refreshKey={logKey} override={history} />

        <footer className="footer">
          <a
            href="https://x.com/ZeroEdgeWheel"
            target="_blank"
            rel="noreferrer"
            className="footer-x"
            aria-label="Follow WHEEL on X"
            title="Follow @ZeroEdgeWheel on X"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <div className="footer-links">
            <a href="#how">HOW TO PLAY</a> · <a href="#recent">RECENT SPINS</a>
          </div>
          <p className="footer-terms">
            <strong>Terms &amp; disclaimer.</strong> $WHEEL is an experimental, community-built
            game of chance on Robinhood Chain — <strong>not affiliated with, endorsed by, or
            connected to Robinhood Markets, Inc.</strong> Nothing here is financial, investment,
            tax, or legal advice. Bets and $WHEEL tokens carry risk and may lose all their value;
            every on-chain transaction is <strong>final and irreversible</strong>. The game and
            contracts are provided <strong>&ldquo;as is&rdquo;, with no warranties of any kind</strong> —
            you use them entirely at your own risk, and the creators, contributors, and operators
            accept <strong>no liability</strong> for any loss, damage, downtime, or bug. You alone
            are responsible for confirming that participating in games of chance and using crypto
            are legal in your jurisdiction and that you meet the minimum legal age; the game is not
            offered where prohibited. Randomness is provided by a third-party VRF and the site may
            go offline at any time. <strong>Never play with more than you can afford to lose. Play
            responsibly.</strong>
          </p>
        </footer>
      </main>

      <ChatBox
        address={address}
        isConnected={isConnected}
        onConnect={connectWallet}
        profiles={profiles}
        onProfilesLoaded={setProfiles}
        onEditProfile={() => {
          setProfileFirstTime(false);
          setProfileOpen(true);
        }}
      />

      {walletPicker && !isConnected && (
        <div className="modal-backdrop" onClick={() => setWalletPicker(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">CONNECT A WALLET</h3>
            <p className="modal-sub">
              any EVM wallet works on {activeChain.name}
              {!walletConnectAvailable && " · set VITE_WALLETCONNECT_PROJECT_ID to add mobile wallets"}
            </p>
            <div className="wallet-options">
              {connectors.map((c) => (
                <button
                  key={c.uid}
                  className="wallet-option"
                  disabled={isConnecting}
                  onClick={() => {
                    connect({ connector: c, chainId: activeChain.id });
                    setWalletPicker(false);
                  }}
                >
                  {c.icon && <img src={c.icon} alt="" className="wallet-icon" />}
                  <span className="wallet-option-name">{c.name}</span>
                  <span className="wallet-option-kind">
                    {c.id === "walletConnect"
                      ? "scan with any mobile wallet"
                      : c.id === "coinbaseWalletSDK"
                        ? "extension or mobile"
                        : "browser extension"}
                  </span>
                </button>
              ))}
            </div>
            <div className="modal-btns">
              <button className="chat-mini-btn" onClick={() => setWalletPicker(false)}>
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {celebration !== null && (
        <WinCelebration
          payout={celebration.payout}
          multiplier={celebration.multiplier}
          totalBet={celebration.totalBet}
          handle={myProfile?.xVerified ? myProfile.xHandle : undefined}
          avatarUrl={myProfile?.avatarUrl || undefined}
          onDone={() => setCelebration(null)}
        />
      )}

      {loseResult !== null && (
        <LoseCelebration result={loseResult} onDone={() => setLoseResult(null)} />
      )}

      {adminOpen && isAdmin && <AdminPanel onClose={() => setAdminOpen(false)} />}

      {profileOpen && address && (
        <ProfileModal
          address={address}
          existing={myProfile}
          firstTime={profileFirstTime}
          onClose={() => setProfileOpen(false)}
          onSaved={() => {
            setProfileOpen(false);
            void fetchProfiles().then(setProfiles).catch(() => undefined);
          }}
        />
      )}
    </div>
  );
}
